// Pure color helpers for the character appearance pipeline. Extracted
// from `src/shell/characterAppearance.ts` as a mechanical lift — zero
// behavior change. See `tests/appearanceColors.test.ts` for the pinning
// characterization suite that documents why there are intentionally
// TWO copies of `hslToRgbNormalized` in the appearance pipeline (this
// one round3's channels; the copy in `src/appearance/baseCharacter.ts`
// does NOT).

export interface CharacterColorSwap {
  from: number;
  to: number;
}

// HSL16 → linear RGB unit. Divisors `/64, /8, /128` and the +2/0/-2 hueTimesSix
// formula match the extractor at `RSPK/tools/extract-character-appearance.mjs`
// `hslToRgb` exactly. The runtime per-face HSL16 lookup converts via the same
// function so its output aligns with the pre-baked `colors` field on every
// model — base body colours decode the same way they did at extraction time.
export function hslToRgbNormalized(hsl: number): [number, number, number] {
  const hue = ((hsl >> 10) & 63) / 64;
  const saturation = ((hsl >> 7) & 7) / 8;
  const lightness = (hsl & 127) / 128;

  const brightness = lightness > 0.5
    ? lightness * (1 + saturation) - saturation * lightness + saturation
    : lightness * (1 + saturation);
  if (brightness <= 0) return [0, 0, 0];

  const t = 2 * lightness - brightness;
  const hueTimesSix = hue * 6;
  const channel = (value: number): number => {
    let sample = value;
    if (sample < 0) sample += 6;
    if (sample >= 6) sample -= 6;
    if (sample < 1) return t + (brightness - t) * sample;
    if (sample < 3) return brightness;
    if (sample < 4) return t + (brightness - t) * (4 - sample);
    return t;
  };

  return [
    round3(channel(hueTimesSix + 2)),
    round3(channel(hueTimesSix)),
    round3(channel(hueTimesSix - 2)),
  ];
}

export function colorKey(r: number, g: number, b: number): string {
  return `${round3(r)}:${round3(g)}:${round3(b)}`;
}

export function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

export function createColorRemap(recolors: CharacterColorSwap[]): Map<string, [number, number, number]> {
  const remap = new Map<string, [number, number, number]>();
  for (const recolor of recolors) {
    const from = hslToRgbNormalized(recolor.from);
    const to = hslToRgbNormalized(recolor.to);
    remap.set(colorKey(...from), to);
  }
  return remap;
}

/** Build an HSL16 → HSL16 remap. Used for per-face recoloring at the source-key
 *  level — preferred over RGB-keyed lookup whenever `faceColors` is available
 *  on a model entry. Mirrors RSPK test-harness.ts buildPart. */
export function createHslRemap(recolors: CharacterColorSwap[]): Map<number, number> {
  const remap = new Map<number, number>();
  for (const recolor of recolors) {
    remap.set(recolor.from & 0xffff, recolor.to & 0xffff);
  }
  return remap;
}
